import {
    Button,
    DrawerProps,
    Group,
    Select,
    Stack,
    TextInput,
} from '@mantine/core';
import {PlatformDrawer} from '@flexinet/ui-components';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    CardsFilters,
    useCardsFilter,
} from '../hooks/useCardsFilter';

/**
 * FNP-918: the cards filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useCardsFilter), not component state, so the table — a
 * sibling, not a child — filters the same list.
 *
 * ⚠️ Two fields are offered: `brand` and `country`. Both are low-cardinality
 * values an operator picks from rather than types, which is what makes a filter
 * control worth having over the keyword box. Everything else on a Card is
 * either an id or free text, already covered by keyword search — a filter for
 * those would duplicate it.
 *
 * `brand` is a Select over the brands Stripe actually issues; `country` stays a
 * TextInput because it is a two-letter ISO code with ~200 values, and a
 * 200-item dropdown is worse than typing "US".
 *
 * ⚠️ Filtering is CLIENT-SIDE over the loaded window — `listCards`' filter
 * argument carries only the customerId scope. See useCards.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useCardsFilter();

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Cards')}
            size='md'
            {...rest}
        >
            <Formik<CardsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Brand')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    'Visa',
                                    'Mastercard',
                                    'American Express',
                                    'Discover',
                                    'Diners Club',
                                    'JCB',
                                    'UnionPay',
                                ]}
                                value={values.brand ?? null}
                                onChange={(value) =>
                                    setFieldValue('brand', value ?? '')}
                            />

                            <TextInput
                                label={I18n.get('Country')}
                                placeholder={I18n.get('Two-letter code, e.g. US')}
                                value={values.country ?? ''}
                                onChange={(event) =>
                                    setFieldValue(
                                        'country',
                                        event.currentTarget.value,
                                    )}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </PlatformDrawer>
    );
};
